import { Box, Flex, FormLabel, Icon, Stack, Text, useTheme, useToken } from "@chakra-ui/react";
import { animate, motion, useMotionValue, useTransform } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiChevronUp, FiChevronDown } from "react-icons/fi";
import { useSlide } from "../public/slide";
import { OptionPropsCommon, SelectorValue, SlidePropsSlider } from "../public/types";
import { SliderState, useQuizActions, useQuizSnapshot } from "./state";
import { MyFormLabel } from "./ui";

function parseColor(css: string): [number, number, number] {
  const hex = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(css);
  if (hex) return [parseInt(hex[1], 16), parseInt(hex[2], 16), parseInt(hex[3], 16)];
  const rgb = /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/.exec(css);
  if (rgb) return [+rgb[1], +rgb[2], +rgb[3]];
  try {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 1;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = css;
    ctx.fillRect(0, 0, 1, 1);
    const { data } = ctx.getImageData(0, 0, 1, 1);
    return [data[0], data[1], data[2]];
  } catch {
    return [128, 128, 128];
  }
}

function lighten(c: [number, number, number], t: number): [number, number, number] {
  return [
    Math.round(c[0] + (255 - c[0]) * t),
    Math.round(c[1] + (255 - c[1]) * t),
    Math.round(c[2] + (255 - c[2]) * t),
  ];
}

function darken(c: [number, number, number], t: number): [number, number, number] {
  return [Math.round(c[0] * (1 - t)), Math.round(c[1] * (1 - t)), Math.round(c[2] * (1 - t))];
}

function interpolateRgb(
  c0: [number, number, number],
  c1: [number, number, number],
  c2: [number, number, number],
  t: number,
): [number, number, number] {
  const p = Math.max(0, Math.min(1, t));
  if (p <= 0.5) {
    const s = p * 2;
    return [
      Math.round(c0[0] + (c1[0] - c0[0]) * s),
      Math.round(c0[1] + (c1[1] - c0[1]) * s),
      Math.round(c0[2] + (c1[2] - c0[2]) * s),
    ];
  }
  const s = (p - 0.5) * 2;
  return [
    Math.round(c1[0] + (c2[0] - c1[0]) * s),
    Math.round(c1[1] + (c2[1] - c1[1]) * s),
    Math.round(c1[2] + (c2[2] - c1[2]) * s),
  ];
}

const KNOB_R = 14;
const OFFSET = 10;
const HIT_PADDING = 24;
const ITEM_H = 60;
const SPRING = { type: "spring" as const, stiffness: 400, damping: 30 };

type SliderInnerProps = {
  options: OptionPropsCommon[];
  currentIdx: number;
  onSelect: (idx: number) => void;
  onDisplayChange?: (idx: number) => void;
  onProgressChange?: (value: number) => void;
  showOptionLabels?: boolean;
  trackColors?: [string, string, string];
  trackBgColor?: string;
  tickColors?: { active: string; inactive: string };
  labelColors?: { active: string; inactive: string };
  handleColor?: string;
  arrowColor?: string;
  fullTrack?: boolean;
};

function getOptionValue(option: OptionPropsCommon) {
  return option.value ?? option.text;
}

function HorizontalSlider({
  options,
  currentIdx,
  onSelect,
  onDisplayChange,
  onProgressChange,
  showOptionLabels = true,
  trackColors,
  trackBgColor,
  tickColors,
  labelColors,
  handleColor,
  arrowColor = "gray",
  fullTrack = false,
}: SliderInnerProps) {
  const theme = useTheme();
  const [resolvedHandleColor] = useToken("colors", [handleColor ?? "brand.500"]);
  const n = options.length;
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const xMotion = useMotionValue(0);
  const shadowProgress = useMotionValue(n > 1 ? currentIdx / (n - 1) : 0);

  const usableWidth = Math.max(0, containerWidth - KNOB_R * 2 - OFFSET * 2);
  const getPosX = useCallback(
    (idx: number) => (n <= 1 ? 0 : (idx / (n - 1)) * usableWidth),
    [n, usableWidth],
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setContainerWidth(el.offsetWidth));
    ro.observe(el);
    setContainerWidth(el.offsetWidth);
    return () => ro.disconnect();
  }, []);

  const activeTrackWidth = useTransform(xMotion, (v) => `${KNOB_R + OFFSET + v}px`);

  const [displayIdx, setDisplayIdx] = useState(currentIdx);
  const lastProgressRef = useRef<number | null>(null);

  useEffect(() => {
    setDisplayIdx(currentIdx);
  }, [currentIdx]);

  useEffect(() => {
    return xMotion.on("change", (v) => {
      const normalized = usableWidth > 0 ? Math.max(0, Math.min(1, v / usableWidth)) : 0;
      const idx = Math.round(normalized * (n - 1));
      setDisplayIdx(idx);
      onDisplayChange?.(idx);
      shadowProgress.set(normalized);
      const progress = Math.round(normalized * 100);
      if (progress !== lastProgressRef.current) {
        lastProgressRef.current = progress;
        onProgressChange?.(progress);
      }
    });
  }, [usableWidth, n]);

  useEffect(() => {
    animate(xMotion, getPosX(currentIdx), SPRING);
  }, [currentIdx, usableWidth]);

  const handleDragEnd = () => {
    const x = xMotion.get();
    const normalized = usableWidth > 0 ? Math.max(0, Math.min(1, x / usableWidth)) : 0;
    const idx = Math.round(normalized * (n - 1));
    onSelect(idx);
    animate(xMotion, getPosX(idx), SPRING);
  };

  const trackRgbs = useMemo(
    () =>
      trackColors?.map(parseColor) as
        | [[number, number, number], [number, number, number], [number, number, number]]
        | undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trackColors?.[0], trackColors?.[1], trackColors?.[2]],
  );

  const getActiveRgb = (p: number): [number, number, number] => {
    const base =
      trackRgbs && !handleColor
        ? interpolateRgb(trackRgbs[0], trackRgbs[1], trackRgbs[2], p)
        : parseColor(resolvedHandleColor);
    return lighten(base, 0.25);
  };

  const dynamicHandleColor = useTransform(shadowProgress, (p) => {
    const [r, g, b] = getActiveRgb(p);
    return `rgb(${r}, ${g}, ${b})`;
  });

  const knobShadow = useTransform(shadowProgress, (p) => {
    const [r, g, b] = getActiveRgb(p);
    const blur = p * 61;
    const spread = p * 24;
    const outerBlur = p * 102;
    const outerSpread = p * 41;
    const ring = `0 0 0 3px rgba(${r}, ${g}, ${b}, ${(0.2 + p * 0.4).toFixed(2)})`;
    if (p < 0.01) return ring;
    return `${ring}, 0 0 ${blur}px ${spread}px rgba(${r}, ${g}, ${b}, 0.6), 0 0 ${outerBlur}px ${outerSpread}px rgba(${r}, ${g}, ${b}, 0.25)`;
  });

  const activeColorAtIdx = (idx: number) => {
    if (trackRgbs) {
      const [r, g, b] = darken(
        interpolateRgb(trackRgbs[0], trackRgbs[1], trackRgbs[2], idx / Math.max(1, n - 1)),
        0.2,
      );
      return `rgb(${r}, ${g}, ${b})`;
    }
    return theme.colors.brand[200];
  };

  return (
    <Box ref={containerRef} w="full" py={2}>
      <Box position="relative" h={`${KNOB_R * 2}px`}>
        {/* Track background */}
        <Box
          position="absolute"
          top="50%"
          left={0}
          right={0}
          h="14px"
          bg={fullTrack && trackColors ? undefined : (trackBgColor ?? "whiteAlpha.200")}
          style={
            fullTrack && trackColors
              ? {
                  background: `linear-gradient(to right, ${trackColors[0]}, ${trackColors[1]}, ${trackColors[2]})`,
                }
              : undefined
          }
          borderRadius="full"
          transform="translateY(-50%)"
        />
        {/* Active track — wrapper clips the gradient without squeezing it */}
        {!fullTrack && (
          <motion.div
            style={{
              width: activeTrackWidth,
              position: "absolute",
              top: "50%",
              left: 0,
              height: "14px",
              transform: "translateY(-50%)",
              overflow: "hidden",
              borderRadius: "9999px",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                width: containerWidth,
                background: trackColors
                  ? `linear-gradient(to right, ${trackColors[0]}, ${trackColors[1]}, ${trackColors[2]})`
                  : theme.colors.brand[500],
              }}
            />
          </motion.div>
        )}
        {/* Tick marks */}
        {containerWidth > 0 &&
          options.map((_, idx) => (
            <Box
              key={idx}
              position="absolute"
              top="50%"
              left={`${KNOB_R + OFFSET + getPosX(idx)}px`}
              w="3px"
              h="14px"
              bg={
                idx <= displayIdx
                  ? (tickColors?.active ?? activeColorAtIdx(displayIdx))
                  : (tickColors?.inactive ?? "whiteAlpha.300")
              }
              borderRadius="1px"
              transform="translate(-50%, -50%)"
              cursor="pointer"
              zIndex={1}
              onClick={() => onSelect(idx)}
              transition="background-color 0.2s"
            />
          ))}
        {/* Knob */}
        {containerWidth > 0 && (
          <motion.div
            drag="x"
            dragConstraints={{ left: 0, right: usableWidth }}
            dragElastic={0}
            dragMomentum={false}
            onDragEnd={handleDragEnd}
            style={{
              x: xMotion,
              position: "absolute",
              top: -HIT_PADDING,
              left: OFFSET - HIT_PADDING,
              width: KNOB_R * 2 + HIT_PADDING * 2,
              height: KNOB_R * 2 + HIT_PADDING * 2,
              cursor: "grab",
              zIndex: 2,
              touchAction: "none",
              userSelect: "none",
            }}
          >
            <motion.div
              style={{
                position: "absolute",
                top: HIT_PADDING,
                left: HIT_PADDING,
                width: KNOB_R * 2,
                height: KNOB_R * 2,
                borderRadius: "50%",
                backgroundColor: dynamicHandleColor,
                boxShadow: knobShadow,
                borderWidth: "2px",
                borderStyle: "solid",
                borderColor: dynamicHandleColor,
                pointerEvents: "none",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  right: "calc(100% + 4px)",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  height: "25px",
                  width: "25px",
                }}
              >
                <Icon as={FiChevronLeft} color={arrowColor} height={"25px"} width={"25px"} />
              </div>

              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "calc(100% + 4px)",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  height: "25px",
                  width: "25px",
                }}
              >
                <Icon as={FiChevronRight} color={arrowColor} height={"25px"} width={"25px"} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </Box>

      {/* Labels */}
      {showOptionLabels && containerWidth > 0 && (
        <Box position="relative" mt={2} h="40px">
          {options.map((option, idx) => (
            <Box
              key={idx}
              position="absolute"
              top={0}
              left={`${KNOB_R + OFFSET + getPosX(idx)}px`}
              transform="translateX(-50%)"
              textAlign="center"
              cursor="pointer"
              onClick={() => onSelect(idx)}
              maxW="70px"
            >
              <Text
                fontSize="xs"
                color={
                  idx === displayIdx
                    ? (labelColors?.active ?? activeColorAtIdx(displayIdx))
                    : (labelColors?.inactive ?? "whiteAlpha.500")
                }
                fontWeight={idx === displayIdx ? "semibold" : "normal"}
                transition="color 0.2s"
                lineHeight={1.2}
              >
                {option.text}
              </Text>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function VerticalSlider({
  options,
  currentIdx,
  onSelect,
  onDisplayChange,
  onProgressChange,
  showOptionLabels = true,
  trackColors,
  trackBgColor,
  tickColors,
  labelColors,
  handleColor,
  arrowColor = "gray",
  fullTrack = false,
}: SliderInnerProps) {
  const theme = useTheme();
  const [resolvedHandleColor] = useToken("colors", [handleColor ?? "brand.500"]);
  const n = options.length;
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setContainerHeight(el.offsetHeight));
    ro.observe(el);
    setContainerHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, []);

  const itemH =
    containerHeight > 0 && n > 1 ? (containerHeight - KNOB_R * 2 - OFFSET * 2) / (n - 1) : ITEM_H;
  const usableHeight = Math.max(0, (n - 1) * itemH);
  const trackAreaHeight =
    containerHeight > 0 ? containerHeight : usableHeight + KNOB_R * 2 + OFFSET * 2;

  const getPosY = useCallback((idx: number) => (n - 1 - idx) * itemH, [n, itemH]);

  const yMotion = useMotionValue(getPosY(currentIdx));
  const shadowProgress = useMotionValue(n > 1 ? currentIdx / (n - 1) : 0);

  const activeTrackHeight = useTransform(
    yMotion,
    (v) => `${trackAreaHeight - KNOB_R - OFFSET - v}px`,
  );

  const [displayIdx, setDisplayIdx] = useState(currentIdx);
  const lastProgressRef = useRef<number | null>(null);

  useEffect(() => {
    setDisplayIdx(currentIdx);
  }, [currentIdx]);

  useEffect(() => {
    return yMotion.on("change", (v) => {
      const normalized = usableHeight > 0 ? Math.max(0, Math.min(1, v / usableHeight)) : 0;
      const idx = Math.round((1 - normalized) * (n - 1));
      setDisplayIdx(idx);
      onDisplayChange?.(idx);
      shadowProgress.set(1 - normalized);
      const progress = Math.round((1 - normalized) * 100);
      if (progress !== lastProgressRef.current) {
        lastProgressRef.current = progress;
        onProgressChange?.(progress);
      }
    });
  }, [usableHeight, n]);

  useEffect(() => {
    animate(yMotion, getPosY(currentIdx), SPRING);
  }, [currentIdx, usableHeight]);

  const handleDragEnd = () => {
    const y = yMotion.get();
    const normalized = usableHeight > 0 ? Math.max(0, Math.min(1, y / usableHeight)) : 0;
    const idx = Math.round((1 - normalized) * (n - 1));
    onSelect(idx);
    animate(yMotion, getPosY(idx), SPRING);
  };

  const trackRgbs = useMemo(
    () =>
      trackColors?.map(parseColor) as
        | [[number, number, number], [number, number, number], [number, number, number]]
        | undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trackColors?.[0], trackColors?.[1], trackColors?.[2]],
  );

  const getActiveRgb = (p: number): [number, number, number] => {
    const base =
      trackRgbs && !handleColor
        ? interpolateRgb(trackRgbs[0], trackRgbs[1], trackRgbs[2], p)
        : parseColor(resolvedHandleColor);
    return lighten(base, 0.25);
  };

  const dynamicHandleColor = useTransform(shadowProgress, (p) => {
    const [r, g, b] = getActiveRgb(p);
    return `rgb(${r}, ${g}, ${b})`;
  });

  const knobShadow = useTransform(shadowProgress, (p) => {
    const [r, g, b] = getActiveRgb(p);
    const blur = p * 61;
    const spread = p * 24;
    const outerBlur = p * 102;
    const outerSpread = p * 41;
    const ring = `0 0 0 3px rgba(${r}, ${g}, ${b}, ${(0.2 + p * 0.4).toFixed(2)})`;
    if (p < 0.01) return ring;
    return `${ring}, 0 0 ${blur}px ${spread}px rgba(${r}, ${g}, ${b}, 0.6), 0 0 ${outerBlur}px ${outerSpread}px rgba(${r}, ${g}, ${b}, 0.25)`;
  });

  const activeColorAtIdx = (idx: number) => {
    if (trackRgbs) {
      const [r, g, b] = darken(
        interpolateRgb(trackRgbs[0], trackRgbs[1], trackRgbs[2], idx / Math.max(1, n - 1)),
        0.2,
      );
      return `rgb(${r}, ${g}, ${b})`;
    }
    return theme.colors.brand[200];
  };

  return (
    <Flex w="full" h="full" gap={4} align="stretch" justify="center">
      {/* Track column */}
      <Box ref={containerRef} position="relative" w={`${KNOB_R * 2}px`} h="full" flexShrink={0}>
        {/* Track background */}
        <Box
          position="absolute"
          left="50%"
          top={0}
          h="full"
          w="14px"
          bg={fullTrack && trackColors ? undefined : (trackBgColor ?? "whiteAlpha.200")}
          style={
            fullTrack && trackColors
              ? {
                  background: `linear-gradient(to top, ${trackColors[0]}, ${trackColors[1]}, ${trackColors[2]})`,
                }
              : undefined
          }
          borderRadius="full"
          transform="translateX(-50%)"
        />
        {/* Active track — wrapper clips the gradient without squeezing it */}
        {!fullTrack && containerHeight > 0 && (
          <motion.div
            style={{
              height: activeTrackHeight,
              position: "absolute",
              left: "50%",
              bottom: 0,
              width: "14px",
              transform: "translateX(-50%)",
              overflow: "hidden",
              borderRadius: "9999px",
            }}
          >
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: trackAreaHeight,
                background: trackColors
                  ? `linear-gradient(to top, ${trackColors[0]}, ${trackColors[1]}, ${trackColors[2]})`
                  : theme.colors.brand[500],
              }}
            />
          </motion.div>
        )}
        {/* Tick marks */}
        {containerHeight > 0 &&
          options.map((_, idx) => (
            <Box
              key={idx}
              position="absolute"
              left="50%"
              top={`${KNOB_R + OFFSET + getPosY(idx)}px`}
              w="14px"
              h="3px"
              bg={
                idx <= displayIdx
                  ? (tickColors?.active ?? activeColorAtIdx(displayIdx))
                  : (tickColors?.inactive ?? "whiteAlpha.300")
              }
              borderRadius="1px"
              transform="translate(-50%, -50%)"
              cursor="pointer"
              zIndex={1}
              onClick={() => onSelect(idx)}
              transition="background-color 0.2s"
            />
          ))}
        {/* Knob */}
        {containerHeight > 0 && (
          <motion.div
            drag="y"
            dragConstraints={{ top: 0, bottom: usableHeight }}
            dragElastic={0}
            dragMomentum={false}
            onDragEnd={handleDragEnd}
            style={{
              y: yMotion,
              position: "absolute",
              top: OFFSET - HIT_PADDING,
              left: -HIT_PADDING,
              width: KNOB_R * 2 + HIT_PADDING * 2,
              height: KNOB_R * 2 + HIT_PADDING * 2,
              cursor: "grab",
              zIndex: 2,
              touchAction: "none",
              userSelect: "none",
            }}
          >
            <motion.div
              style={{
                position: "absolute",
                top: HIT_PADDING,
                left: HIT_PADDING,
                width: KNOB_R * 2,
                height: KNOB_R * 2,
                borderRadius: "50%",
                backgroundColor: dynamicHandleColor,
                boxShadow: knobShadow,
                borderWidth: "2px",
                borderStyle: "solid",
                borderColor: dynamicHandleColor,
                pointerEvents: "none",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: "50%",
                  bottom: "calc(100% + 4px)",
                  transform: "translateX(-50%)",
                  pointerEvents: "none",
                  height: "25px",
                  width: "25px",
                }}
              >
                <Icon as={FiChevronUp} color={arrowColor} height={"25px"} width={"25px"} />
              </div>
              <div
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "calc(100% + 4px)",
                  transform: "translateX(-50%)",
                  pointerEvents: "none",
                  height: "25px",
                  width: "25px",
                }}
              >
                <Icon as={FiChevronDown} color={arrowColor} height={"25px"} width={"25px"} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </Box>

      {/* Labels column */}
      {showOptionLabels && (
        <Box position="relative" h="full">
          {containerHeight > 0 &&
            options.map((option, idx) => (
              <Box
                key={idx}
                position="absolute"
                top={`${KNOB_R + OFFSET + getPosY(idx)}px`}
                transform="translateY(-50%)"
                cursor="pointer"
                onClick={() => onSelect(idx)}
              >
                <Text
                  fontSize="sm"
                  color={
                    idx === displayIdx
                      ? (labelColors?.active ?? activeColorAtIdx(displayIdx))
                      : (labelColors?.inactive ?? "whiteAlpha.600")
                  }
                  fontWeight={idx === displayIdx ? "semibold" : "normal"}
                  transition="color 0.2s"
                >
                  {option.text}
                </Text>
              </Box>
            ))}
        </Box>
      )}
    </Flex>
  );
}

export type SliderSelectProps = SlidePropsSlider & {
  onTempValueChange?: (value: SelectorValue) => void;
  onProgressChange?: (value: number) => void;
};

export function SliderSelect(props: SliderSelectProps) {
  const actions = useQuizActions();
  const slideCtx = useSlide();
  const snap = useQuizSnapshot();

  const selectorState = snap.slideStateByID[slideCtx.id] as SliderState | undefined;
  const options = props.options ?? [];

  useEffect(() => {
    if (!selectorState || selectorState.value != null || options.length === 0) return;
    const defaultIdx =
      props.defaultValue != null
        ? Math.max(0, Math.min(props.defaultValue, options.length - 1))
        : 0;
    const option = options[defaultIdx];
    actions.setSliderValue(slideCtx.id, { value: getOptionValue(option), idx: defaultIdx });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!selectorState]);

  const currentIdx = selectorState?.value?.idx ?? props.defaultValue ?? 0;
  const isHorizontal = (props.variant ?? "horizontal") === "horizontal";
  const [liveIdx, setLiveIdx] = useState(currentIdx);

  useEffect(() => {
    setLiveIdx(currentIdx);
  }, [currentIdx]);

  const handleSelect = useCallback(
    (idx: number) => {
      const option = options[idx];
      if (!option) return;
      actions.setSliderValue(slideCtx.id, { value: getOptionValue(option), idx });
    },
    [options, slideCtx.id],
  );

  const lastTempIdx = useRef<number | null>(null);

  const handleDisplayChange = useCallback(
    (idx: number) => {
      setLiveIdx(idx);
      if (idx !== lastTempIdx.current) {
        lastTempIdx.current = idx;
        const option = options[idx];
        if (option) {
          props.onTempValueChange?.({ value: getOptionValue(option), idx });
        }
      }
    },
    [options, props.onTempValueChange],
  );

  const selectedText = options[liveIdx]?.text;

  const trackRgbs = useMemo(
    () =>
      props.trackColors?.map(parseColor) as
        | [[number, number, number], [number, number, number], [number, number, number]]
        | undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.trackColors?.[0], props.trackColors?.[1], props.trackColors?.[2]],
  );

  const selectedLabelColor = (() => {
    if (props.selectedLabelColor) return props.selectedLabelColor;
    if (trackRgbs) {
      const [r, g, b] = lighten(
        interpolateRgb(
          trackRgbs[0],
          trackRgbs[1],
          trackRgbs[2],
          liveIdx / Math.max(1, options.length - 1),
        ),
        0.25,
      );
      return `rgb(${r}, ${g}, ${b})`;
    }
    return "brand.200";
  })();

  return (
    <Stack spacing={1} w="full" h={isHorizontal ? undefined : "full"}>
      {props.label && <MyFormLabel mx="auto">{props.label}</MyFormLabel>}
      {props.showSelectedLabel && (
        <Text fontSize="lg" fontWeight="semibold" color={selectedLabelColor} textAlign="center">
          {selectedText}
        </Text>
      )}

      <FormLabel
        textAlign={"center"}
        fontWeight={"normal"}
        color="whiteAlpha.600"
        fontSize={"sm"}
        lineHeight={1}
        mx={0}
        pb={2}
      >
        {isHorizontal ? "(slide left or right)" : "(slide up or down)"}
      </FormLabel>

      <Box w="full" flex={isHorizontal ? undefined : 1} minH={0}>
        {isHorizontal ? (
          <HorizontalSlider
            options={options}
            currentIdx={currentIdx}
            onSelect={handleSelect}
            onDisplayChange={handleDisplayChange}
            onProgressChange={props.onProgressChange}
            showOptionLabels={props.showOptionLabels}
            trackColors={props.trackColors}
            trackBgColor={props.trackBgColor}
            tickColors={props.tickColors}
            labelColors={props.labelColors}
            handleColor={props.handleColor}
            arrowColor={props.arrowColor}
            fullTrack={props.fullTrack}
          />
        ) : (
          <VerticalSlider
            options={options}
            currentIdx={currentIdx}
            onSelect={handleSelect}
            onDisplayChange={handleDisplayChange}
            onProgressChange={props.onProgressChange}
            showOptionLabels={props.showOptionLabels}
            trackColors={props.trackColors}
            trackBgColor={props.trackBgColor}
            tickColors={props.tickColors}
            labelColors={props.labelColors}
            handleColor={props.handleColor}
            arrowColor={props.arrowColor}
            fullTrack={props.fullTrack}
          />
        )}
      </Box>
    </Stack>
  );
}
